// Small layout primitives shared across admin screens.
import type { ReactNode } from 'react';
import { cn } from '../../../lib/cn';

export function PageTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-5">
      <h1 className="text-[26px] font-extrabold tracking-tight md:text-[30px]">{title}</h1>
      {sub && <p className="mt-0.5 text-[14px] text-ink2">{sub}</p>}
    </div>
  );
}

export function StatCard({
  label,
  value,
  sub,
  accent
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-black/5 bg-card p-4 shadow-tile md:p-5">
      <div className="text-[11px] font-semibold uppercase tracking-eyebrow text-ink2">{label}</div>
      <div className={cn('num mt-1.5 text-[28px] font-extrabold tracking-tight md:text-[34px]', accent && 'text-accent-ink')}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[13px] text-ink2">{sub}</div>}
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
  className,
  bodyClassName
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('overflow-hidden rounded-2xl border border-black/5 bg-card shadow-tile', className)}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-black/5 px-4 py-3 md:px-5">
          {title && <h2 className="text-[15px] font-bold tracking-tight">{title}</h2>}
          {action}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

type PillTone = 'neutral' | 'accent' | 'gold' | 'danger' | 'dark';

export function Pill({ children, tone = 'neutral' }: { children: ReactNode; tone?: PillTone }) {
  const tones: Record<PillTone, string> = {
    neutral: 'bg-fill text-ink2',
    accent: 'bg-accent-wash text-accent-ink',
    gold: 'bg-gold-top/40 text-gold-ink',
    danger: 'bg-miss/15 text-danger',
    dark: 'bg-night text-white'
  };
  return (
    <span className={cn('num inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold', tones[tone])}>
      {children}
    </span>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="px-4 py-12 text-center text-[14px] text-ink2">{children}</div>;
}

export function Th({
  children,
  className,
  onClick,
  active
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <th
      className={cn(
        'whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide',
        onClick && 'cursor-pointer select-none hover:text-ink',
        active ? 'text-ink' : 'text-ink2',
        className
      )}
      onClick={onClick}
    >
      {children}
    </th>
  );
}

export function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn('whitespace-nowrap px-3 py-3 align-middle text-[13px] text-ink', className)}>{children}</td>;
}

export type { PillTone };
